import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import useModals from 'src/Hooks/useModals'
import { Theme, useMediaQuery } from '@mui/material'
import { PageActions } from '@e3dc-react/shell/State/App/AppModel'
import {
  defaultDashboardAreasConfiguration,
  defaultDynamicPowerPriceWidgetAreasConfiguration,
  defaultWidgetAreasConfiguration,
  EDIT_SEARCH_PARAMETER,
} from '../consts'
import { UserSettings } from 'src/Libs/user/types'
import { DashboardAreasConfiguration, DashboardConfiguration, WidgetAreasConfiguration } from '../types'
import { useTypedSelector } from 'src/State/RootReducer'
import PageActionsContext from 'src/Libs/header/context/page-actions.context'
import { E3EditPage } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'
import { DASHBOARD_EDITOR_BUTTON_ID } from 'src/Libs/qa/ids'
import { useUserSettings } from 'src/Hooks'
import { getInstallationDashboardUrlByIds } from '../routes'

const useStyles = makeStyles()(() => ({
  editButton: {
    '&.MuiSvgIcon-root': {
      width: 35,
      height: 35,
    },
  },
}))

/**
 * dashboard editor custom hook to add edit button to the header and handle the editor state via url search parameters
 */
const useDashboardEditor = (
  installationId?: string,
  storageId?: string,
  userSettings?: UserSettings,
  isDynamicPowerPriceEnabled?: boolean
): DashboardConfiguration => {
  const { openDashboardEditor } = useModals()
  const navigate = useNavigate()
  const location = useLocation()
  const [editorOpen, setEditorOpen] = useState<boolean>(false)
  const [editorOpened, setEditorOpened] = useState<boolean>(false)
  const dashboardEditingAvailable = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))
  const { dispatchPageActions } = useContext(PageActionsContext)
  const [dashboardAreasConfiguration, setDashboardAreasConfiguration] = useState<DashboardAreasConfiguration>(defaultDashboardAreasConfiguration)
  const [widgetAreasConfiguration, setWidgetAreasConfiguration] = useState<WidgetAreasConfiguration>(
    isDynamicPowerPriceEnabled ? defaultDynamicPowerPriceWidgetAreasConfiguration : defaultWidgetAreasConfiguration
  )
  const [fetchedSettingsApplied, setFetchedSettingsApplied] = useState<boolean>(false)
  const { updateUserSettings } = useUserSettings()

  /** isDynamicPowerPriceEnabled is not directly ready, so we need to update the widgetAreas afterwards */
  useEffect(() => {
    if (isDynamicPowerPriceEnabled && !fetchedSettingsApplied) {
      setWidgetAreasConfiguration(defaultDynamicPowerPriceWidgetAreasConfiguration)
    }
  }, [isDynamicPowerPriceEnabled, fetchedSettingsApplied])

  const userId = useTypedSelector(state => state.user.userData?.ID)
  const { classes } = useStyles()

  useEffect(() => {
    if (userSettings?.dashboardConfiguration) {
      const { dashboardAreas, widgetAreas } = JSON.parse(userSettings.dashboardConfiguration)

      setDashboardAreasConfiguration(dashboardAreas)
      setWidgetAreasConfiguration(widgetAreas)
      setFetchedSettingsApplied(true)
    }
  }, [userSettings])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const isEdit = searchParams.get('edit') === 'true'

    if (!editorOpen && isEdit && dashboardEditingAvailable && userSettings) {
      setEditorOpen(true)
    } else if (editorOpen && !isEdit) {
      setEditorOpen(false)
      setEditorOpened(false)
    }
  }, [location.search, editorOpen, dashboardEditingAvailable, userSettings, isDynamicPowerPriceEnabled])

  const saveAreasConfiguration = useCallback(
    async (dashboardConfiguration: DashboardConfiguration): Promise<void> => {
      return updateUserSettings({
        dashboardConfiguration: JSON.stringify({
          dashboardAreas: dashboardConfiguration.dashboardAreasConfiguration,
          widgetAreas: dashboardConfiguration.widgetAreasConfiguration,
        }),
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, isDynamicPowerPriceEnabled]
  )

  useEffect(() => {
    if (editorOpen && !editorOpened) {
      openDashboardEditor({
        dashboardAreasConfiguration,
        widgetAreasConfiguration,
        saveAreasConfiguration: async (
          dashboardAreasConfiguration: DashboardAreasConfiguration,
          widgetAreasConfiguration: WidgetAreasConfiguration
        ): Promise<void> => {
          return saveAreasConfiguration({ dashboardAreasConfiguration, widgetAreasConfiguration })
        },
        isDynamicPowerPriceEnabled,
      })
      setEditorOpened(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardAreasConfiguration, editorOpen, saveAreasConfiguration, widgetAreasConfiguration, editorOpened, isDynamicPowerPriceEnabled])

  useEffect(() => {
    const dashboardActions: PageActions = dashboardEditingAvailable
      ? [
          {
            position: 'right',
            type: 'custom',
            onClick: () => {
              navigate(`${getInstallationDashboardUrlByIds(installationId, storageId)}${EDIT_SEARCH_PARAMETER}`, {
                replace: true,
              })
            },
            icon: <E3EditPage className={classes.editButton} data-qa={DASHBOARD_EDITOR_BUTTON_ID} />,
          },
        ]
      : []

    if (installationId && storageId && userSettings) {
      dispatchPageActions?.(dashboardActions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardEditingAvailable, installationId, storageId, navigate, userSettings])

  useEffect(() => {
    return () => {
      dispatchPageActions?.([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    dashboardAreasConfiguration,
    widgetAreasConfiguration,
  }
}

export default useDashboardEditor
